/**
 * <ilva-carousel> — ILVA scroll-carousel navigation (dots + side arrows).
 *
 * Generalises the navigation logic first shipped inline in
 * sections/collection-list.liquid so it can be reused across every ILVA
 * scroll carousel. It drives page-based scrolling of a `.slider`
 * scroll-snap container and owns the dots, the side arrows and (in the
 * legacy "progress" preset) the thin desktop progress bar.
 *
 * It coexists with the theme's <carousel-slider>: that component keeps
 * ownership of drag-to-scroll, scroll-snap and keyboard behaviour (its own
 * `.slider-nav` stays force-hidden), while this component layers the ILVA
 * page nav on top of the same `.slider`. Neither fights the other because
 * only this component ever programmatically scrolls on nav interaction.
 *
 * Expected DOM (rendered by snippets/ilva-carousel-nav.liquid):
 *   <ilva-carousel data-nav-style="dots_arrows|progress" data-arrow-target=".card__media">
 *     <div class="… ilva-car__viewport">
 *       <div class="slider slider--no-scrollbar" id="…"> … .slider__item … </div>
 *     </div>
 *     <div class="ilva-car__arrows" data-ilva-car-arrows hidden> prev/next </div>
 *     <div class="ilva-car__controls">
 *       [<div class="ilva-car__progress"><div class="ilva-car__progress-fill"></div></div>]
 *       <div class="ilva-car__dots" role="tablist" hidden></div>
 *     </div>
 *   </ilva-carousel>
 *
 * Data attributes:
 *   data-nav-style   "dots_arrows" (default) — dots + arrows at every breakpoint.
 *                    "progress"    — desktop progress bar + mobile dots + arrows
 *                                    (preserves the original collection-list design).
 *   data-arrow-target  CSS selector, within a slide, whose vertical centre the
 *                      arrows align to (default ".card__media" — the image row).
 *   data-breakpoint    Max-width px below which "progress" mode swaps to dots +
 *                      arrows (default 599.98). Ignored in "dots_arrows" mode.
 *   data-page-label    aria-label template for dots, "%PAGE%" replaced with the
 *                      1-based page number (translated in Liquid, passed in here).
 *   data-slider-wait   Opt-in, no value. When the slider is not in the DOM yet at
 *                      connect, watch this element and initialise once it
 *                      appears — for a scroller a third party renders after load
 *                      (the Relewise app blocks in sections/apps.liquid only
 *                      create theirs once results arrive). Without it a missing
 *                      slider leaves the component inert, as before.
 */
if (!customElements.get('ilva-carousel')) {
  class IlvaCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      // Custom selectors let the component attach to non-standard markup (e.g.
      // the Relewise cart-drawer scroller, or the gallery grids) without
      // restructuring it. Defaults match the theme carousel.
      this.sliderSelector = this.dataset.sliderSelector || '.slider';
      this.itemSelector = this.dataset.itemSelector || '.slider__item';
      this.slider = this.querySelector(this.sliderSelector);
      if (!this.slider) {
        this.waitForSlider();
        return;
      }
      this.initialised = true;

      this.navStyle = this.dataset.navStyle === 'progress' ? 'progress' : 'dots_arrows';
      this.arrowTarget = this.dataset.arrowTarget || '.card__media';
      this.breakpoint = parseFloat(this.dataset.breakpoint) || 599.98;
      this.pageLabel = this.dataset.pageLabel || 'Go to page %PAGE%';
      // Fixed cards-per-page override — pins the paging step instead of
      // deriving it from the measured slide geometry. Used by "peek" layouts
      // (cards sized to a fraction of the viewport so the next one peeks in,
      // e.g. the Relewise cart-drawer scroller) where the merchant-intended
      // step is a property of the design, not of what happens to fit.
      this.fixedPerPage = parseInt(this.dataset.perPage, 10) || 0;

      this.viewport = this.slider.closest('.ilva-car__viewport') || this.slider.parentElement;
      this.arrows = this.querySelector('[data-ilva-car-arrows]');
      // Arrows must overlay the media row, so they belong inside the
      // positioned viewport. They are authored after the slider (so the
      // markup reads top-to-bottom) and relocated here; `hidden` until the
      // first render, so no flash from the move.
      if (this.arrows && this.viewport && this.arrows.parentElement !== this.viewport) {
        this.viewport.appendChild(this.arrows);
      }
      this.prevBtn = this.arrows && this.arrows.querySelector('[name="prev"]');
      this.nextBtn = this.arrows && this.arrows.querySelector('[name="next"]');
      this.dots = this.querySelector('.ilva-car__dots');
      this.progress = this.querySelector('.ilva-car__progress');
      this.fill = this.querySelector('.ilva-car__progress-fill');

      this.scrollEventName = 'onscrollend' in window ? 'scrollend' : 'scroll';
      this.rtl = document.dir === 'rtl';
      // NB: name this `dirSign`, NOT `dir` — `dir` is a native reflected
      // HTMLElement property (ltr/rtl/auto), so `this.dir = 1` coerces to "" and
      // then `"" * offset` is 0, silently scrolling everything to the start.
      this.dirSign = this.rtl ? -1 : 1;

      // Layout metrics, recomputed on resize. slideSpan = distance between the
      // first two cards' leading edges (card width + gap), a rough pitch used
      // only for the reveal threshold. pageStarts = the measured scroll offset
      // of each page's first slide — the authority for paging.
      this.slides = [];
      this.slideSpan = 0;
      this.pageStarts = [0];
      this.totalPages = 1;
      this.ticking = false;

      this.onScroll = this.schedule.bind(this);
      this.onResize = this.rebuild.bind(this);
      this.onLoad = this.rebuild.bind(this);

      if (this.dots) {
        this.dots.addEventListener('click', (evt) => {
          const dot = evt.target.closest('.ilva-car__dot');
          if (dot) this.scrollToPage(parseInt(dot.dataset.page, 10));
        });
      }
      if (this.arrows) {
        this.arrows.addEventListener('click', (evt) => {
          const btn = evt.target.closest('.ilva-car__arrow');
          if (!btn) return;
          const step = btn.name === 'next' ? 1 : -1;
          this.scrollToPage(Math.min(this.totalPages - 1, Math.max(0, this.currentPage() + step)));
        });
      }

      this.slider.addEventListener(this.scrollEventName, this.onScroll);
      if (this.scrollEventName === 'scrollend') this.slider.addEventListener('scroll', this.onScroll);
      window.addEventListener('on:debounced-resize', this.onResize);

      // Re-measure once everything has loaded — the initial rebuild() can run
      // before fonts/images settle the final layout, which could leave the
      // controls shown (or hidden) against stale metrics.
      if (document.readyState !== 'complete') {
        window.addEventListener('load', this.onLoad, { once: true });
      }

      // Robustly re-measure whenever the slider's box changes. Fires once as
      // soon as the slider first gets real layout (the key fix for the
      // intermittent "controls visible against stale metrics") and again on
      // any later resize. The controls live outside the slider, so toggling
      // them can't feed back into this observer.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.slider);
      }

      // Rebuild when slides are injected/updated asynchronously (Relewise
      // recommendation cards, recently-viewed, etc.). Debounced so a burst of
      // innerHTML writes coalesces into one rebuild. Only mutations inside the
      // slider fire this; the dots/arrows live outside it, so it can't self-feed.
      if ('MutationObserver' in window) {
        let mutTimer;
        this.slideObserver = new MutationObserver(() => {
          clearTimeout(mutTimer);
          mutTimer = setTimeout(() => this.rebuild(), 150);
        });
        this.slideObserver.observe(this.slider, { childList: true, subtree: true });
      }

      // Re-centre once the first card image loads — its height may be unknown
      // at init, which would otherwise leave the arrows pinned to 0.
      const firstImg = this.slider.querySelector('img');
      if (firstImg && !firstImg.complete) {
        firstImg.addEventListener('load', () => this.positionArrows(), { once: true });
      }

      this.rebuild();
    }

    // data-slider-wait: initialise once the slider is rendered into this
    // element. One observer at most — a re-connect while still waiting reuses
    // it — and it disconnects itself on success.
    waitForSlider() {
      if (!('sliderWait' in this.dataset) || this.waitObserver || !('MutationObserver' in window)) return;
      this.waitObserver = new MutationObserver(() => {
        if (!this.querySelector(this.sliderSelector)) return;
        this.stopWaiting();
        this.connectedCallback();
      });
      this.waitObserver.observe(this, { childList: true, subtree: true });
    }

    stopWaiting() {
      if (!this.waitObserver) return;
      this.waitObserver.disconnect();
      this.waitObserver = null;
    }

    disconnectedCallback() {
      // Before the guard below: a carousel still waiting for its slider is by
      // definition not initialised, and its observer must not outlive it (a
      // theme-editor re-render, or a band that never gets results).
      this.stopWaiting();
      if (!this.initialised) return;
      this.slider.removeEventListener(this.scrollEventName, this.onScroll);
      this.slider.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('on:debounced-resize', this.onResize);
      window.removeEventListener('load', this.onLoad);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.slideObserver) this.slideObserver.disconnect();
    }

    maxScroll() {
      return Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    }

    measure() {
      // Bail when the slider has no layout (display:none, zero width, or
      // mid-transition). Measuring now would yield slideSpan 0 and a bogus dot
      // count; keep the last good metrics until it's visible.
      if (this.slides.length < 2 || this.slider.clientWidth === 0) {
        if (this.slides.length < 2) { this.slideSpan = 0; this.pageStarts = [0]; this.totalPages = 1; }
        return;
      }

      const rects = this.slides.map((slide) => slide.getBoundingClientRect());
      const first = rects[0];
      this.slideSpan = Math.abs(rects[1].left - first.left) || first.width;

      // Distance along the scroll axis from the content start to each slide's
      // LEADING edge. Rect deltas rather than offsetLeft: invariant to the
      // current scroll position, and correct inside transformed ancestors.
      // In RTL the first slide is the rightmost and content runs leftwards, so
      // the leading edge is the right one — left-to-left deltas would be off by
      // the difference in slide widths.
      const lead = (rect) => (this.rtl ? first.right - rect.right : rect.left - first.left);
      const starts = rects.map(lead);

      // The BLEED — a transparent trailing border on the last slide (see
      // render(), and `.slider:not(.slider--contained) .slider__item:last-child`
      // in main.css) — is a gutter, not content. Counting it toward the row's
      // width would spill the final slide onto a page of its own.
      const last = this.slides[this.slides.length - 1];
      const bleed = parseFloat(getComputedStyle(last).borderInlineEndWidth) || 0;
      const ends = rects.map((rect, i) => starts[i] + rect.width - (i === rects.length - 1 ? bleed : 0));

      // Page boundaries from the REAL geometry rather than a uniform-grid
      // model. The old approach derived slidesPerPage by rounding
      // clientWidth / slideSpan off the first two slides, which silently
      // assumed every slide was the same width: a row of content-sized items
      // (the quick-links pills) sampled its two NARROWEST slides, inflated
      // slidesPerPage, and collapsed totalPages to 1 — so the arrows sat
      // disabled while render() still (correctly) reported real overflow and
      // showed them. Packing greedily against measured edges keeps the page
      // count in agreement with the scroll range by construction. For a
      // uniform grid it reproduces the old boundaries exactly.
      //
      // Budget is the slider's CONTENT-box width, not clientWidth: clientWidth
      // includes horizontal padding, while the offsets above are measured from
      // the first slide (i.e. from inside that padding). A padded scroller —
      // the mobile quick-links list carries `padding: 0 var(--gutter)`, see
      // quick-links.css — would otherwise be credited with a viewport wider
      // than the row can actually occupy, under-counting pages. That reopened
      // the very bug this rewrite closes: real overflow (so render() shows the
      // controls) but a single page (so both arrows sit disabled).
      const style = getComputedStyle(this.slider);
      const padInline = (parseFloat(style.paddingInlineStart) || 0)
        + (parseFloat(style.paddingInlineEnd) || 0);
      const viewport = Math.max(1, this.slider.clientWidth - padInline);
      const pageStarts = [0];
      if (this.fixedPerPage) {
        for (let i = this.fixedPerPage; i < starts.length; i += this.fixedPerPage) {
          pageStarts.push(starts[i]);
        }
      } else {
        for (let i = 1; i < ends.length; i += 1) {
          // +1px slack absorbs sub-pixel layout rounding, which would otherwise
          // spill a slide that visually fits onto a page of its own.
          if (ends[i] - pageStarts[pageStarts.length - 1] > viewport + 1) {
            pageStarts.push(starts[i]);
          }
        }
      }
      // Drop trailing pages whose CLAMPED resting offset would coincide with
      // the previous page's. Only the final page can ever clamp (see
      // pageOffset), but when it does it can land within a pixel of its
      // predecessor — a 6-item row at 370px produces starts 0/260/517/675 that
      // clamp to 0/260/517/518. Those last two are the same position on screen,
      // so keeping both renders a dot that is visually and behaviourally
      // identical to the one before it, and leaves currentPage() ambiguous
      // there. Pruning forfeits at most COINCIDENT_PX of trailing gutter.
      //
      // The 2px tolerance is deliberately wider than currentPage()'s 1px
      // end-of-scroll shortcut, and absorbs the browser rounding scroll
      // positions to device pixels: any page resting within 2px of the end
      // BECOMES the last page, so the shortcut can only ever fire on the page
      // that reports itself. A tighter tolerance leaves a page 1-2px short of
      // the end that reads back as its successor.
      const COINCIDENT_PX = 2;
      const limit = this.maxScroll();
      while (pageStarts.length > 1
        && Math.min(pageStarts[pageStarts.length - 1], limit)
          - Math.min(pageStarts[pageStarts.length - 2], limit) <= COINCIDENT_PX) {
        pageStarts.pop();
      }

      this.pageStarts = pageStarts;
      this.totalPages = pageStarts.length;
    }

    currentPage() {
      const max = this.totalPages - 1;
      if (max < 1) return 0;
      const pos = Math.abs(this.slider.scrollLeft);
      const maxScroll = this.maxScroll();
      // At (~1px from) the end, report the LAST page. A partial final page has
      // its offset clamped to maxScroll (see pageOffset), well short of where
      // its first slide actually starts; measuring raw distance there would
      // favour an earlier page — leaving the last dot never active and the
      // prev/next disabled states inverted at the end.
      if (pos >= maxScroll - 1) return max;
      // Nearest page by its CLAMPED resting offset — the position the slider
      // actually comes to rest at for that page.
      let page = 0;
      let closest = Infinity;
      for (let i = 0; i <= max; i += 1) {
        const distance = Math.abs(Math.min(this.pageStarts[i], maxScroll) - pos);
        if (distance < closest) { closest = distance; page = i; }
      }
      return page;
    }

    buildDots() {
      // Idempotent: only rebuild when the page count actually changed, so a
      // horizontal resize that doesn't cross a card-count boundary doesn't
      // thrash the DOM or flicker the active dot.
      if (!this.dots || this.dots.children.length === this.totalPages) return;
      this.dots.textContent = '';
      for (let i = 0; i < this.totalPages; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ilva-car__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', this.pageLabel.replace('%PAGE%', i + 1));
        dot.dataset.page = i;
        this.dots.appendChild(dot);
      }
    }

    // Scroll offset (magnitude, always ≥ 0) for a given page, clamped to the
    // real scroll range. Critical with scroll-snap-type: mandatory — a target
    // that overshoots maxScroll can't resolve to an in-range snap point and the
    // browser snaps back to the start (0). A partial last page would therefore
    // jump to page 0. Clamping keeps the last page resting at the true end.
    // Only the FINAL page can ever need clamping: greedy packing starts a new
    // page only for a slide that does NOT fit the current one, so if everything
    // from slide k onward fits one viewport (i.e. starts[k] > maxScroll) no
    // page is opened after k. That rules out two pages sharing a resting
    // offset, which is what previously produced an unreachable dead dot.
    // `dirSign` applies the sign for RTL at the call site.
    pageOffset(page) {
      return Math.min(this.pageStarts[page] || 0, this.maxScroll());
    }

    scrollToPage(page) {
      this.slider.scrollTo({ left: this.dirSign * this.pageOffset(page), behavior: 'smooth' });
    }

    schedule() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(this.render.bind(this));
    }

    render() {
      this.ticking = false;

      // True overflow, ignoring the carousel BLEED. A bleed carousel widens
      // the slider and gives the last item a transparent right border of the
      // same width, so raw `scrollWidth - clientWidth` reports phantom
      // overflow even when every card fits. The last item's resolved right
      // border equals the bleed (concrete px, unlike the unresolved calc()
      // custom prop); subtract it so a full row reports zero scrollable
      // distance. With no bleed the border is 0 — a safe no-op.
      const lastItem = this.slides[this.slides.length - 1];
      const bleed = lastItem ? parseFloat(getComputedStyle(lastItem).borderInlineEndWidth) || 0 : 0;
      const scrollable = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth - bleed);

      // Treat the carousel as scrollable only when more than a SLIVER of a card
      // is hidden (~15%). This ignores the few-pixel near-fit a full row can
      // leave at the viewport edge (sub-pixel rounding / the bleed model),
      // which shouldn't surface the controls — while still revealing them as
      // soon as a card is genuinely cut off. Falls back to 1px until slideSpan
      // is known.
      const revealThreshold = this.slideSpan > 1 ? this.slideSpan * 0.15 : 1;
      const multiPage = this.slides.length >= 2 && scrollable > revealThreshold;

      // Reveal/hide controls. Both groups carry `hidden`; CSS (per nav-style +
      // breakpoint) decides which is actually shown.
      if (this.arrows) this.arrows.hidden = !multiPage;
      if (this.dots) this.dots.hidden = !multiPage;
      if (this.progress) this.progress.style.display = multiPage ? '' : 'none';
      if (!multiPage) return;

      // Desktop progress bar (progress mode only) — shows the VISIBLE fraction
      // of the content, so on load at the first item it already reads as
      // partially filled rather than empty.
      if (this.fill) {
        const visibleEnd = Math.abs(this.slider.scrollLeft) + this.slider.clientWidth;
        const pct = Math.min(100, (visibleEnd / (scrollable + this.slider.clientWidth)) * 100);
        this.fill.style.width = `${pct}%`;
      }

      const page = this.currentPage();
      if (this.dots) {
        const children = this.dots.children;
        for (let i = 0; i < children.length; i += 1) {
          children[i].setAttribute('aria-selected', i === page ? 'true' : 'false');
        }
      }
      if (this.prevBtn) this.prevBtn.disabled = page <= 0;
      if (this.nextBtn) this.nextBtn.disabled = page >= this.totalPages - 1;

      // Centre the arrows on the media row. Measured here (not in rebuild)
      // because the arrows must be VISIBLE for offsetParent/rects to be
      // non-zero — render has just un-hidden them above.
      this.positionArrows();
    }

    positionArrows() {
      // Centre arrows on the target row (default the media, not the whole card
      // which includes the label below). Arrows are absolutely placed against
      // the viewport; measure the target's centre relative to that same box via
      // rect deltas. Bail if geometry is not ready (hidden / not laid out) so
      // we never pin to 0.
      if (!this.arrows || this.arrows.hidden) return;
      const target = this.slider.querySelector(this.arrowTarget);
      const anchor = this.arrows.offsetParent;
      if (!target || !anchor) return;
      const targetHeight = target.getBoundingClientRect().height;
      if (targetHeight < 1) return;
      const center = target.getBoundingClientRect().top
        - anchor.getBoundingClientRect().top
        + targetHeight / 2;
      anchor.style.setProperty('--ilva-car-arrow-center', `${center}px`);
    }

    rebuild() {
      // Remember the current page (using the OLD metrics) so a resize that
      // changes card width doesn't leave the slider parked between pages, out
      // of sync with the active dot.
      const prevPage = this.currentPage();

      this.slides = [...this.slider.querySelectorAll(this.itemSelector + ':not([hidden])')];
      this.measure();
      this.buildDots();

      // Re-snap to the same page at the new metrics so the slider stays aligned
      // with the active dot. Only when snapping is authoritative: always in
      // dots_arrows mode, mobile-only in progress mode (desktop progress mode
      // uses free scroll, where snapping on resize would feel abrupt). Jump
      // (auto), never animate on resize.
      const isMobile = window.matchMedia(`(max-width: ${this.breakpoint}px)`).matches;
      const snap = this.navStyle === 'dots_arrows' || isMobile;
      if (this.pageStarts.length > 1 && snap) {
        const page = Math.min(this.totalPages - 1, prevPage);
        this.slider.scrollTo({ left: this.dirSign * this.pageOffset(page), behavior: 'auto' });
      }

      this.render();
    }
  }

  customElements.define('ilva-carousel', IlvaCarousel);
}
